import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import Uploader from 'components/Uploader';
import { getAlertasVehiculares } from 'api/recaudacion';
import { getConfiguracionesDeSegmentosDeFolios } from 'api/configuracion/configuracionesDeFolios';
import { getOrigenesDeAlertas } from 'api/configuracion';
import { getCatalogoDePadrones } from 'api/catalogos';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';

const { TextArea } = Input;

export const model = 'alerta';
export const catalogsModels = ['origendelaalerta', 'padron', 'configuraciondesegmentofolio'];
export const permissionConfig = { model, catalogsModels };

const AlertasDelVehiculo = ({
  vehiculoId,
}) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);

  const [folios, setFolios] = useState([]);
  const [origenesDeAlertas, setOrigenesDeAlertas] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await getCatalogoDePadrones();
      const authorizedParams = { estados_globales: 4 };
      const _folios = await getConfiguracionesDeSegmentosDeFolios(authorizedParams);
      const _origenes = await getOrigenesDeAlertas(authorizedParams);
      const _data = await getAlertasVehiculares(vehiculoId);
      if (mounted) {
        setFolios(_folios);
        setOrigenesDeAlertas(_origenes);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const renderTitle = (e) => `${e.departamento.clave}${e.id} - ${e.departamento.descripcion}`;

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
    },
    {
      titleText: '¿Es restrictiva?',
      dataIndex: 'es_restrictiva',
      key: 'es_restrictiva',
      width: 200,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Fecha de Cierre',
      dataIndex: 'fecha_de_cierre',
      key: 'fecha_de_cierre',
      width: 200,
      render: (val) => (val?.format(DATE_FRONTEND_FORMAT)),
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickExpand: () => setVisible(true),
            }}
            allowExpand
            mobileColIndex={0}
            disabled
          />
        ) : (
          <Card
            className="form-container"
            title="Alarma"
            extra={(
              <FormSubmitControls onCancel={onCancel} />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="descripcion"
                    rules={rules.required}
                    label="Descripción"
                    hasFeedback
                  >
                    <TextArea autoSize={{ minRows: 4, maxRows: 5 }} allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    hasFeedback
                  >
                    <TextArea autoSize={{ minRows: 4, maxRows: 5 }} allowClear disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="origen"
                    rules={rules.required}
                    label="Origen de Alerta"
                    hasFeedback
                  >
                    <Select dataSource={origenesDeAlertas} render={renderTitle} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="padron"
                    rules={rules.required}
                    label="Padrón"
                    hasFeedback
                  >
                    <Select dataSource={padrones} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="folio"
                    rules={rules.required}
                    label="Folio"
                    hasFeedback
                  >
                    <Select dataSource={folios} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="es_informativa"
                    label="¿Es Informativa?"
                    hasFeedback
                  >
                    <Select trueFalse disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="estados_globales"
                    label="Estado"
                    hasFeedback
                    rules={rules.required}
                  >
                    <Select
                      disabled
                      dataSource={estadosGlobales}
                    />
                  </Form.Item>
                </Col>
                <Uploader
                  title="Documento"
                  limitMB={50}
                  formItemName="documento"
                  allowedExtensions={['pdf', 'docx', 'doc']}
                  rules={rules.documento}
                  allowPreview
                />
              </Row>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
      </Spin>
    </Row>
  );
};

AlertasDelVehiculo.propTypes = {
  vehiculoId: PropTypes.number,
};

AlertasDelVehiculo.defaultProps = {
  vehiculoId: null,
};

export default AlertasDelVehiculo;
